import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { ordersService } from '../services/firebaseService';
import { PaymentModal } from '../components/PaymentModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { useToast } from '../components/Toast';
import {
  Truck, Clock, CheckCircle, MapPin, Navigation,
  RefreshCw, LogOut, Phone, Package, CreditCard,
  ChevronRight, ArrowLeft, AlertCircle, ChefHat
} from 'lucide-react';

export const DeliveryMode = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const wakeLockRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔐 H-03 FIX Build 305: Vérification de session avec token HMAC serveur
  useEffect(() => {
    const session = localStorage.getItem('delivery_mode_session');
    const expiry = localStorage.getItem('delivery_mode_expiry');

    if (!session || !expiry) {
      navigate('/delivery-mode-login', { replace: true });
      return;
    }

    // Vérifier que le token HMAC serveur est valide
    try {
      const tokenData = JSON.parse(atob(session));
      if (!tokenData.mode || !tokenData.created || !tokenData.expiry || !tokenData.sig) {
        throw new Error('Token invalide — champs manquants');
      }
      if (tokenData.mode !== 'delivery') {
        throw new Error('Token invalide — mauvais mode');
      }
      if (tokenData.expiry <= Date.now()) {
        throw new Error('Token expiré');
      }
    } catch (e) {
      localStorage.removeItem('delivery_mode_session');
      localStorage.removeItem('delivery_mode_expiry');
      navigate('/delivery-mode-login', { replace: true });
      return;
    }

    const expiryDate = new Date(expiry);
    if (expiryDate <= new Date()) {
      localStorage.removeItem('delivery_mode_session');
      localStorage.removeItem('delivery_mode_expiry');
      navigate('/delivery-mode-login', { replace: true });
    }
  }, [navigate]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Helper pour vérifier si une commande est payée (inclut cashback/fidélité)
  const isOrderPaid = (order) => {
    if (!order) return false;
    if (order.payment_status === 'paid') return true;
    if (order.is_paid === true) return true;

    // Paiements par cashback/fidélité
    const cashbackMethods = ['cashback', 'loyalty', 'fidelite', 'fidélité', 'loyalty_points', 'loyalty_only'];
    if (cashbackMethods.includes(order.payment_method?.toLowerCase())) return true;

    // Commande à 0€ payée par points fidélité
    if ((order.total === 0 || order.total === '0' || order.total === 0.00) &&
        (order.loyalty_used > 0 || order.cashback_used > 0)) {
      return true;
    }

    return false;
  };

  // Wake Lock
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeLockActive(true);
        wakeLockRef.current.addEventListener('release', () => setWakeLockActive(false));
      }
    } catch (err) {
      console.error('Wake Lock error:', err);
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setWakeLockActive(false);
      } catch (err) {
        console.error('Wake Lock release error:', err);
      }
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        await requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const session = localStorage.getItem('delivery_mode_session');
    const expiry = localStorage.getItem('delivery_mode_expiry');

    if (!session || !expiry || new Date(expiry) < new Date()) {
      navigate('/delivery-mode-login');
      return;
    }

    requestWakeLock();
    loadOrders();
    const interval = setInterval(loadOrders, 5000); // Refresh plus fréquent
    return () => {
      clearInterval(interval);
      releaseWakeLock();
    };
  }, [navigate]);

  const loadOrders = async () => {
    try {
      // FIREBASE DIRECT
      const response = await ordersService.getActive();
      // Inclure aussi les commandes livrées pour les afficher dans la colonne "Livrées"
      const deliveryOrders = (response?.orders || []).filter(
        o => o.order_type === 'delivery' && ['new', 'in_preparation', 'ready', 'delivering', 'delivered'].includes(o.status)
      );
      // Trier par pickup_time (heure de retrait la plus proche en premier)
      const getPickupDateTime = (order) => {
        if (order.pickup_date && order.pickup_time) {
          return `${order.pickup_date} ${order.pickup_time}`;
        }
        return order.created_at || '';
      };
      deliveryOrders.sort((a, b) => getPickupDateTime(a).localeCompare(getPickupDateTime(b)));
      setOrders(deliveryOrders);

      // Mettre à jour selectedOrder si elle existe
      if (selectedOrder) {
        const updated = deliveryOrders.find(o => o.id === selectedOrder.id);
        if (updated) {
          setSelectedOrder(updated);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Configuration des confirmations par statut
  const getConfirmationConfig = (newStatus) => {
    const configs = {
      'in_preparation': {
        title: 'Commencer la préparation ?',
        message: 'La commande passera en statut "En préparation".',
        type: 'warning',
        confirmText: 'Oui, commencer'
      },
      'ready': {
        title: 'Marquer comme prête ?',
        message: 'La commande sera prête à être livrée.',
        type: 'success',
        confirmText: 'Oui, prête'
      },
      'delivering': {
        title: 'Partir en livraison ?',
        message: 'La commande passera en statut "En livraison".',
        type: 'warning',
        confirmText: 'Oui, partir'
      },
      'delivered': {
        title: 'Confirmer la livraison ?',
        message: 'La commande sera marquée comme livrée et terminée.',
        type: 'success',
        confirmText: 'Oui, livrée'
      }
    };
    return configs[newStatus] || { title: 'Confirmer ?', message: '', type: 'warning', confirmText: 'Confirmer' };
  };

  // Demander confirmation avant changement de statut
  const requestStatusChange = (newStatus, order = null) => {
    const targetOrder = order || selectedOrder;

    // Vérifier paiement avant de livrer
    if (newStatus === 'delivered' && !isOrderPaid(targetOrder)) {
      toast.error('Paiement requis', 'Veuillez encaisser avant de terminer la livraison');
      setSelectedOrder(targetOrder);
      setShowPaymentModal(true);
      return;
    }

    // Si on a passé une commande spécifique (depuis la liste), la sélectionner
    if (order) {
      setSelectedOrder(order);
    }

    setPendingStatusChange(newStatus);
    setShowConfirmModal(true);
  };

  // Changement de statut rapide depuis la liste (AVEC modal de confirmation)
  const handleQuickStatusChange = async (e, order, newStatus) => {
    e.stopPropagation(); // Empêche l'ouverture du détail

    // Vérifier paiement avant de livrer
    if (newStatus === 'delivered' && !isOrderPaid(order)) {
      toast.error('Paiement requis', 'Veuillez encaisser avant de terminer la livraison');
      setSelectedOrder(order);
      setShowPaymentModal(true);
      return;
    }

    // Demander confirmation via le modal
    setSelectedOrder(order);
    setPendingStatusChange(newStatus);
    setShowConfirmModal(true);
  };

  // Ouvrir le modal de paiement depuis la liste
  const handleQuickPayment = (e, order) => {
    e.stopPropagation();
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  // Confirmer le changement de statut
  const confirmStatusChange = async () => {
    if (!pendingStatusChange || !selectedOrder) return;

    setUpdatingStatus(true);
    setShowConfirmModal(false);

    try {
      // FIREBASE DIRECT
      await ordersService.updateStatus(selectedOrder.id, pendingStatusChange);

      // TOUJOURS fermer la modal après changement de statut réussi
      setSelectedOrder(null);

      await loadOrders();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur', 'Erreur lors de la mise à jour');
    } finally {
      setUpdatingStatus(false);
      setPendingStatusChange(null);
    }
  };

  const handleLogout = () => {
    releaseWakeLock();
    localStorage.removeItem('delivery_mode_session');
    localStorage.removeItem('delivery_mode_expiry');
    // Also clear admin session to require PIN for Back Office access
    localStorage.removeItem('admin_session');
    localStorage.removeItem('admin_session_expiry');
    navigate('/select-mode');
  };

  const openGoogleMaps = (address) => {
    if (!address) return;
    const query = encodeURIComponent(`${address.street}, ${address.postal_code} ${address.city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  const callCustomer = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': '🆕 Nouvelle',
      'in_preparation': '👨‍🍳 En préparation',
      'ready': '✅ Prête',
      'delivering': '🚚 En livraison',
      'delivered': '✅ Livrée'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-red-500',
      'in_preparation': 'bg-orange-500',
      'ready': 'bg-yellow-500',
      'delivering': 'bg-blue-500',
      'delivered': 'bg-green-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getNextStatus = (status) => {
    const flow = {
      'new': 'in_preparation',
      'in_preparation': 'ready',
      'ready': 'delivering',
      'delivering': 'delivered'
    };
    return flow[status] || null;
  };

  const getNextAction = (status) => {
    const actions = {
      'new': '🔥 Commencer prépa',
      'in_preparation': '✅ Prête',
      'ready': '🚚 Partir livrer',
      'delivering': '✓ Livrée'
    };
    return actions[status] || null;
  };

  // Configuration des 3 colonnes Kanban
  const DELIVERY_COLUMNS = [
    {
      id: 'preparing',
      label: '🍳 En Préparation',
      statuses: ['new', 'in_preparation', 'ready'],
      bgColor: 'bg-orange-500',
      lightBg: 'bg-orange-50',
      borderColor: 'border-orange-300',
      headerBg: 'bg-gradient-to-r from-orange-500 to-amber-500'
    },
    {
      id: 'delivering',
      label: '🚚 En Livraison',
      statuses: ['delivering'],
      bgColor: 'bg-blue-500',
      lightBg: 'bg-blue-50',
      borderColor: 'border-blue-300',
      headerBg: 'bg-gradient-to-r from-blue-500 to-indigo-500'
    },
    {
      id: 'delivered',
      label: '✅ Livrées',
      statuses: ['delivered'],
      bgColor: 'bg-green-500',
      lightBg: 'bg-green-50',
      borderColor: 'border-green-300',
      headerBg: 'bg-gradient-to-r from-green-500 to-emerald-500'
    }
  ];

  // Stats
  const preparingCount = orders.filter(o => ['new', 'in_preparation', 'ready'].includes(o.status)).length;
  const deliveringCount = orders.filter(o => o.status === 'delivering').length;
  const deliveredCount = orders.filter(o => o.status === 'delivered').length;
  const unpaidCount = orders.filter(o => !isOrderPaid(o) && o.status !== 'delivered').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Vue détail d'une commande
  if (selectedOrder) {
    const nextStatus = getNextStatus(selectedOrder.status);
    const nextAction = getNextAction(selectedOrder.status);
    const isPaid = isOrderPaid(selectedOrder);
    const confirmConfig = pendingStatusChange ? getConfirmationConfig(pendingStatusChange) : {};

    return (
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-4 sticky top-0 z-10 shadow-lg">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            <button
              onClick={() => setSelectedOrder(null)}
              className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="text-center">
              <p className="text-white/80 text-sm">Commande</p>
              <h1 className="font-black text-2xl">#{selectedOrder.order_number}</h1>
            </div>
            <div className="w-12"></div>
          </div>
        </div>

        <div className="p-4 space-y-4 pb-36 max-w-3xl mx-auto">
          {/* Statut avec couleur */}
          <div className={`p-5 rounded-2xl text-white ${getStatusColor(selectedOrder.status)}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedOrder.status === 'delivered' && <CheckCircle className="w-8 h-8" />}
                {selectedOrder.status === 'delivering' && <Truck className="w-8 h-8" />}
                {selectedOrder.status === 'ready' && <CheckCircle className="w-8 h-8" />}
                {selectedOrder.status === 'in_preparation' && <ChefHat className="w-8 h-8" />}
                {selectedOrder.status === 'new' && <Package className="w-8 h-8" />}
                <div>
                  <p className="text-white/80 text-sm">Statut actuel</p>
                  <p className="font-bold text-xl">{getStatusLabel(selectedOrder.status)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Client */}
          <Card className="p-5 bg-white shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white font-black text-2xl shadow-lg">
                {(selectedOrder.customer_name || 'C')[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="text-gray-500 text-sm">Client</p>
                <p className="font-black text-2xl text-gray-800">{selectedOrder.customer_name}</p>
              </div>
            </div>

            {selectedOrder.customer_phone && (
              <button
                onClick={() => callCustomer(selectedOrder.customer_phone)}
                className="w-full flex items-center justify-between p-4 bg-green-50 border-2 border-green-200 rounded-xl hover:bg-green-100 active:scale-[0.98] transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-gray-500 text-sm">Appeler</p>
                    <span className="font-bold text-green-800 text-xl">{selectedOrder.customer_phone}</span>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-green-600" />
              </button>
            )}
          </Card>

          {/* Adresse */}
          <Card className="p-5 bg-white shadow-lg">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-blue-600" />
              <h3 className="font-bold text-lg text-gray-800">Adresse de livraison</h3>
            </div>

            {selectedOrder.delivery_address ? (
              <>
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="font-bold text-xl text-gray-800">{selectedOrder.delivery_address.street}</p>
                  <p className="text-gray-600 text-lg mt-1">
                    {selectedOrder.delivery_address.postal_code} {selectedOrder.delivery_address.city}
                  </p>
                  {selectedOrder.delivery_address.notes && (
                    <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-orange-700 font-medium">
                        📝 {selectedOrder.delivery_address.notes}
                      </p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => openGoogleMaps(selectedOrder.delivery_address)}
                  className="w-full flex items-center justify-center gap-3 p-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-lg shadow-lg hover:from-blue-600 hover:to-blue-700 active:scale-[0.98] transition-all"
                >
                  <Navigation className="w-6 h-6" />
                  Ouvrir dans Google Maps
                </button>
              </>
            ) : (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-red-500" />
                <p className="text-red-700 font-bold text-lg">Adresse non renseignée</p>
              </div>
            )}
          </Card>

          {/* Paiement */}
          <Card className={`p-5 shadow-lg ${isPaid ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-300'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {isPaid ? (
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                    <AlertCircle className="w-7 h-7 text-white" />
                  </div>
                )}
                <div>
                  <p className="text-gray-500 text-sm">Paiement</p>
                  <p className={`font-bold text-xl ${isPaid ? 'text-green-700' : 'text-red-700'}`}>
                    {isPaid ? 'Déjà payé' : 'À encaisser'}
                  </p>
                </div>
              </div>
              <span className="font-black text-3xl text-gray-800">{selectedOrder.total?.toFixed(2)}€</span>
            </div>

            {!isPaid && (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="w-full flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg hover:from-green-600 hover:to-emerald-700 active:scale-[0.98] transition-all"
              >
                <CreditCard className="w-6 h-6" />
                Encaisser le paiement
              </button>
            )}

            {isPaid && selectedOrder.payment_method && (
              <p className="text-green-700 font-medium">
                Mode : {selectedOrder.payment_method === 'cash' ? '💵 Espèces' :
                  selectedOrder.payment_method === 'card' ? '💳 Carte bancaire' :
                    selectedOrder.payment_method}
              </p>
            )}
          </Card>

          {/* Articles */}
          <Card className="p-5 bg-white shadow-lg">
            <h3 className="font-bold text-lg text-gray-800 mb-4">
              🛒 Articles ({selectedOrder.items?.length || 0})
            </h3>
            <div className="space-y-2">
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between py-3 border-b last:border-0">
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {item.quantity}
                    </span>
                    <div>
                      <span className="font-semibold text-gray-800">{item.name}</span>
                      {item.options?.length > 0 && (
                        <p className="text-sm text-gray-400 mt-1">{item.options.map(o => o.name || o).join(', ')}</p>
                      )}
                    </div>
                  </div>
                  <span className="font-bold text-gray-800">{((item.total_price != null ? item.total_price : (item.unit_price || item.price || item.base_price || 0) * (item.quantity || 1)) || 0).toFixed(2)}€</span>
                </div>
              ))}
            </div>
          </Card>

          {selectedOrder.notes && (
            <Card className="p-5 bg-yellow-50 border-2 border-yellow-300 shadow-lg">
              <h3 className="font-bold text-lg mb-2 text-yellow-800">📝 Note du client</h3>
              <p className="text-yellow-900 text-lg">{selectedOrder.notes}</p>
            </Card>
          )}
        </div>

        {/* Footer fixe avec action principale */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 p-4 shadow-2xl safe-area-inset-bottom">
          <div className="max-w-3xl mx-auto">
            {nextStatus && nextAction && (
              <Button
                onClick={() => requestStatusChange(nextStatus)}
                disabled={updatingStatus}
                className={`w-full py-5 text-xl rounded-xl shadow-lg ${
                  nextStatus === 'delivered'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                    : nextStatus === 'delivering'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                      : 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700'
                }`}
              >
                {updatingStatus ? (
                  <span className="animate-pulse">Mise à jour...</span>
                ) : (
                  nextAction
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Modal de Paiement */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          order={selectedOrder}
          onSuccess={() => {
            loadOrders();
            setShowPaymentModal(false);
            // Fermer aussi la vue détail après paiement réussi
            setSelectedOrder(null);
          }}
        />

        {/* Modal de Confirmation */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setPendingStatusChange(null);
          }}
          onConfirm={confirmStatusChange}
          title={confirmConfig.title}
          message={confirmConfig.message}
          type={confirmConfig.type}
          confirmText={confirmConfig.confirmText}
        />
      </div>
    );
  }

  // Liste des commandes
  return (
    <div className="min-h-screen bg-gray-900 overflow-x-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-white/20 rounded-xl md:rounded-2xl flex items-center justify-center">
              <Truck className="w-6 h-6 md:w-8 md:h-8" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold">Mode Livraison</h1>
              <p className="text-white/80 text-sm">
                {orders.length} livraison{orders.length > 1 ? 's' : ''}
                {wakeLockActive && ' 🔆'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadOrders}
              className="border-white/30 text-white hover:bg-white/20 min-h-[44px] min-w-[44px]"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white/30 text-white hover:bg-white/20 min-h-[44px] min-w-[44px]"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      {orders.length > 0 && (
        <div className="px-4 py-3 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-orange-500/20 rounded-xl p-3 text-center">
              <p className="text-orange-400 text-2xl font-black">{preparingCount}</p>
              <p className="text-orange-400/80 text-xs">En prépa</p>
            </div>
            <div className="bg-blue-500/20 rounded-xl p-3 text-center">
              <p className="text-blue-400 text-2xl font-black">{deliveringCount}</p>
              <p className="text-blue-400/80 text-xs">En livraison</p>
            </div>
            <div className="bg-green-500/20 rounded-xl p-3 text-center">
              <p className="text-green-400 text-2xl font-black">{deliveredCount}</p>
              <p className="text-green-400/80 text-xs">Livrées</p>
            </div>
            {unpaidCount > 0 && (
              <div className="bg-red-500/20 rounded-xl p-3 text-center animate-pulse">
                <p className="text-red-400 text-2xl font-black">{unpaidCount}</p>
                <p className="text-red-400/80 text-xs">À encaisser</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kanban 3 colonnes */}
      <div className="p-4 max-w-7xl mx-auto flex-1 overflow-hidden">
        {orders.length === 0 ? (
          <div className="flex items-center justify-center h-[60vh]">
            <Card className="text-center py-16 px-12 bg-gray-800 border-gray-700">
              <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-12 h-12 text-gray-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-400">Aucune livraison en attente</h3>
              <p className="text-gray-500 mt-2">Les commandes à livrer apparaîtront ici</p>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">
            {DELIVERY_COLUMNS.map((column) => {
              const columnOrders = orders.filter(o => column.statuses.includes(o.status));

              return (
                <div
                  key={column.id}
                  className={`flex flex-col rounded-xl ${column.lightBg} border-2 ${column.borderColor} overflow-hidden`}
                >
                  {/* Header colonne */}
                  <div className={`${column.headerBg} text-white p-4`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-lg">{column.label}</span>
                      <span className="bg-white/20 px-3 py-1 rounded-full font-black text-xl">
                        {columnOrders.length}
                      </span>
                    </div>
                  </div>

                  {/* Liste des commandes */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {columnOrders.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-sm">Aucune commande</p>
                      </div>
                    ) : (
                      columnOrders.map((order) => {
                        const nextStatus = getNextStatus(order.status);
                        const nextAction = getNextAction(order.status);
                        const isPaid = isOrderPaid(order);

                        return (
                          <div
                            key={order.id}
                            className={`bg-white rounded-xl shadow-md border-l-4 p-3 cursor-pointer hover:shadow-lg transition-all ${
                              order.status === 'delivered' ? 'border-green-500' :
                              order.status === 'delivering' ? 'border-blue-500' :
                              order.status === 'ready' ? 'border-yellow-500' :
                              order.status === 'in_preparation' ? 'border-orange-500' :
                              'border-red-500'
                            }`}
                            onClick={() => setSelectedOrder(order)}
                          >
                            {/* Numéro + Paiement */}
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-black text-lg text-gray-800">#{order.order_number}</span>
                              {order.status !== 'delivered' && (
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  isPaid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {isPaid ? '✅ Payé' : '💰 À encaisser'}
                                </span>
                              )}
                              {order.status === 'delivered' && (
                                <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700">
                                  🚚 Livrée
                                </span>
                              )}
                            </div>

                            {/* Client */}
                            <p className="font-semibold text-gray-700 truncate">{order.customer_name}</p>

                            {/* Statut actuel (pour la colonne préparation qui a plusieurs statuts) */}
                            {column.id === 'preparing' && (
                              <p className="text-xs text-gray-500 mt-1">{getStatusLabel(order.status)}</p>
                            )}

                            {/* Adresse */}
                            {order.delivery_address && (
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1 truncate">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{order.delivery_address.street}</span>
                              </p>
                            )}

                            {/* Heure + Total */}
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {formatTime(order.created_at)}
                              </span>
                              <span className="font-bold text-green-600">{order.total?.toFixed(2)}€</span>
                            </div>

                            {/* Boutons d'action */}
                            {order.status !== 'delivered' && (
                              <div className="flex items-center gap-2 mt-3 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                                {/* Bouton Encaisser */}
                                {!isPaid && (
                                  <button
                                    onClick={(e) => handleQuickPayment(e, order)}
                                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 min-h-[40px] bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold text-xs shadow hover:from-green-600 hover:to-emerald-700 transition-all active:scale-95"
                                  >
                                    <CreditCard className="w-3 h-3" />
                                    Encaisser
                                  </button>
                                )}

                                {/* Bouton action suivante */}
                                {nextStatus && nextAction && (
                                  <button
                                    onClick={(e) => handleQuickStatusChange(e, order, nextStatus)}
                                    disabled={updatingStatus}
                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 min-h-[40px] rounded-lg font-semibold text-xs shadow transition-all text-white active:scale-95 ${
                                      nextStatus === 'delivered'
                                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                                        : nextStatus === 'delivering'
                                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                                          : nextStatus === 'ready'
                                            ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600'
                                            : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
                                    }`}
                                  >
                                    {nextAction}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryMode;
